import React, { useEffect } from 'react';
import { CheckCircle, Calendar, Users, Mail, Phone, Download, Share2, X } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ConfirmationModal = ({ bookingData, onClose }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Prevent background scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  const handleDownloadConfirmation = () => {
    // Create a simple text file with booking details
    const confirmationText = `
BOOKING CONFIRMATION
===================

Booking ID: ${bookingData?.bookingId}
Tour: ${bookingData?.tourName}
Date: ${formatDate(bookingData?.selectedDate)}
Time: ${bookingData?.selectedTime || 'TBD'}
Travelers: ${bookingData?.adults || 0} Adults, ${bookingData?.children || 0} Children
Total: $${bookingData?.totalAmount}

Confirmation Number: ${bookingData?.confirmationNumber}
Payment Method: ${bookingData?.paymentMethod}
Payment Date: ${new Date(bookingData?.paymentDate)?.toLocaleDateString()}

Thank you for booking with TourEase!
    `;

    const element = document.createElement('a');
    const file = new Blob([confirmationText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `booking-confirmation-${bookingData?.bookingId}.txt`;
    document.body?.appendChild(element);
    element?.click();
    document.body?.removeChild(element);
  };

  const handleShareBooking = () => {
    if (navigator.share) {
      navigator.share({
        title: 'My TourEase Booking',
        text: `I just booked ${bookingData?.tourName} for ${formatDate(bookingData?.selectedDate)}!`,
        url: window.location?.href
      });
    } else {
      // Fallback to clipboard
      const shareText = `I just booked ${bookingData?.tourName} for ${formatDate(bookingData?.selectedDate)} with TourEase! Booking ID: ${bookingData?.bookingId}`;
      navigator.clipboard?.writeText(shareText);
      alert('Booking details copied to clipboard!');
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Overlay */}
        <div className="fixed inset-0 transition-opacity bg-gray-900 bg-opacity-50" onClick={onClose}></div>

        {/* Modal */}
        <div className="inline-block w-full max-w-lg p-0 my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Booking Confirmed!</h2>
                <p className="text-sm text-gray-500">Your payment has been processed</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Confirmation Details */}
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-center">
                <h3 className="text-lg font-medium text-green-900 mb-2">
                  Booking Confirmation
                </h3>
                <div className="text-2xl font-mono font-bold text-green-700 mb-1">
                  {bookingData?.confirmationNumber || bookingData?.bookingId}
                </div>
                <p className="text-sm text-green-600">
                  Save this confirmation number for your records
                </p>
              </div>
            </div>

            {/* Booking Summary */}
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="w-12 h-12 bg-gray-200 rounded-lg flex-shrink-0">
                  <img
                    src="/api/placeholder/48/48"
                    alt={bookingData?.tourName}
                    className="w-full h-full object-cover rounded-lg"
                  />
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{bookingData?.tourName}</h4>
                  <div className="text-sm text-gray-500 space-y-1 mt-1">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(bookingData?.selectedDate)}</span>
                    </div>
                    {bookingData?.selectedTime && (
                      <div className="flex items-center space-x-2">
                        <Users className="w-4 h-4" />
                        <span>{bookingData?.selectedTime}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4" />
                      <span>
                        {bookingData?.adults} Adult{bookingData?.adults !== 1 ? 's' : ''}
                        {bookingData?.children > 0 && `, ${bookingData?.children} Child${bookingData?.children !== 1 ? 'ren' : ''}`}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-gray-900">
                    ${bookingData?.totalAmount}
                  </div>
                  <div className="text-sm text-gray-500">
                    {bookingData?.paymentMethod?.replace('-', ' ')?.replace(/\b\w/g, l => l?.toUpperCase())}
                  </div>
                </div>
              </div>
            </div>

            {/* Next Steps */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-3">What's Next?</h4>
              <div className="space-y-2 text-sm text-blue-800">
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>Confirmation email sent to your inbox</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>We'll contact you 24h before your tour</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>Add to calendar reminder sent</span>
                </div>
              </div>
            </div>

            {/* Important Information */}
            <div className="bg-yellow-50 rounded-lg p-4">
              <h4 className="font-medium text-yellow-900 mb-2">Important Information</h4>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>• Please arrive 15 minutes before tour start time</li>
                <li>• Bring a valid photo ID</li>
                <li>• Check weather conditions before departure</li>
                <li>• Free cancellation up to 7 days before tour date</li>
              </ul>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleDownloadConfirmation}
                variant="outline"
                className="flex items-center space-x-2"
                fullWidth
              >
                <Download className="w-4 h-4" />
                <span>Download Confirmation</span>
              </Button>

              <Button
                onClick={handleShareBooking}
                variant="outline"
                className="flex items-center space-x-2"
                fullWidth
              >
                <Share2 className="w-4 h-4" />
                <span>Share Booking</span>
              </Button>
            </div>

            <Button
              onClick={onClose}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              Go to My Bookings
            </Button>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4">
            <p className="text-xs text-gray-500 text-center">
              Questions? Contact us at{' '}
              <a href="tel:+15551234567" className="text-blue-600 hover:text-blue-700">
                +1 (555) 123-4567
              </a>{' '}
              or{' '}
              <a href="mailto:support@tourease.com" className="text-blue-600 hover:text-blue-700">
                support@tourease.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;